import { createFileRoute } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, SectionHeader, CTABand } from "@/components/site/Blocks";
import { buildHead } from "@/lib/seo-head";

export const Route = createFileRoute("/es/como-funciona-dscr")({
  head: () =>
    buildHead({
      title: "Cómo Funciona un Préstamo DSCR | Simply Approved",
      description:
        "Aprenda cómo se calcula el DSCR, por qué no se requieren ingresos personales y qué pautas usan los prestamistas para aprobar estos préstamos.",
      url: "/es/como-funciona-dscr",
      locale: "es",
      keywords: "cómo funciona DSCR, qué es DSCR, calculo DSCR, cobertura servicio deuda, préstamo sin ingresos personales",
      breadcrumbs: [
        { name: "Inicio", url: "/es" },
        { name: "Cómo funciona DSCR", url: "/es/como-funciona-dscr" },
      ],
      service: { name: "Educación sobre préstamos DSCR" },
    }),
  component: HowItWorks,
});

function HowItWorks() {
  return (
    <SiteShell>
      <PageHero eyebrow="Cómo funciona" title="La propiedad paga el préstamo, no su nómina." subtitle="Un préstamo DSCR evalúa la salud financiera de la propiedad en lugar de depender de ingresos personales." />
      <Section>
        <SectionHeader title="La fórmula DSCR" />
        <div className="prose prose-slate mt-6 max-w-none">
          <p>
            <strong>DSCR = Renta mensual bruta ÷ Pago mensual total de la deuda (PITIA)</strong>
          </p>
          <p>
            Si una propiedad alquila por <strong>$3,000</strong> mensuales y su PITIA es{" "}
            <strong>$2,500</strong>, el DSCR es <strong>1.20</strong>. Eso significa que la propiedad
            genera 20% más de lo necesario para cubrir su pago.
          </p>
          <p>
            Un DSCR de <strong>1.0</strong> indica punto de equilibrio: la renta iguala al pago. Muchos
            programas DSCR aceptan ratios menores con compensación de crédito, reservas o amortización
            ajustada, y algunos programas No-Ratio ignoran el ingreso por renta documentado.
          </p>
          <h3>¿Por qué no se requieren ingresos personales?</h3>
          <p>
            Como el préstamo se suscribe como transacción con fines comerciales (business-purpose) en
            una propiedad de inversión, el riesgo se mide por la capacidad de la propiedad de generar
            renta. El prestatario personal o la entidad sigue pasando por revisión de crédito y
            reservas, pero no se usan W-2s, talones de pago ni declaraciones fiscales personales.
          </p>
          <h3>¿Qué es el PITIA?</h3>
          <ul>
            <li><strong>P</strong>rincipal</li>
            <li><strong>I</strong>ntereses</li>
            <li><strong>T</strong>axes (impuestos de propiedad)</li>
            <li><strong>I</strong>nsurance (seguros de propiedad y huracán/inundación si aplica)</li>
            <li><strong>A</strong>ssociation dues (cuotas de HOA, si aplica)</li>
          </ul>
        </div>
      </Section>
      <CTABand title="Calcule su DSCR" body="Use nuestra calculadora para ver la relación renta/PITIA de su propiedad." />
    </SiteShell>
  );
}
